import { BANK_TRANSFER, CAD_ONLINE_BILLPAY } from 'constants/offers';
import { INR_CURRENCY_CODE } from 'constants/ui';
import { Amount } from 'containers/Amount/Amount';
import type { Field as FieldType } from 'models/field';
import type { Logo } from 'models/logo';
import type { PropsWithChildren } from 'react';
import { type ConnectedProps, connect } from 'react-redux';
import type { RootState } from 'reducers/types';
import { getCurrency } from 'selectors/entities/currencies/currencies';
import { formatAmount } from 'utils/money/money';
import { useTranslations } from 'utils/translations/useTranslations';
import { Field } from '../../../Field/Field';
import bankTransferPaymentMethodIcon from './assets/ico-bank-transfer.svg';
import { ReactComponent as InfoIcon } from './assets/ico-info-blue.svg';
import onlinePaymentMethodIcon from './assets/ico-online.svg';
import { Fee } from './components/Fee/Fee';
import { FXInfo } from './components/FXInfo/FXInfo';
import { RecurringLabels } from './components/RecurringLabels/RecurringLabels';
import { SupportedLogos } from './components/SupportedLogos/SupportedLogos';

type OptionProps = ConnectedProps<typeof connector> & {
  exchangeRate?: number;
  fee: {
    value: number;
  };
  interval?: number;
  logo?: Logo;
  logos?: Logo[];
  markupRate?: number;
  name: string;
  paymentMethod?: string;
  paymentOption?: string;
  price: {
    currency: string;
    value: number;
  };
  selected?: boolean;
  onChange?: () => void;
};

const OptionComponent = ({
  children,
  exchangeRate,
  fee,
  interval,
  logo,
  logos = [],
  markupRate,
  name,
  paymentMethod,
  paymentOption,
  price: { currency, value },
  selected = false,
  getCurrency,
  onChange,
}: PropsWithChildren<OptionProps>) => {
  const isBankTransfer = paymentMethod === BANK_TRANSFER;
  const showSupportedLogos =
    paymentOption === CAD_ONLINE_BILLPAY && logos.length > 0;
  const isCurrencyFromINR = currency === INR_CURRENCY_CODE;
  const hasFx = exchangeRate && markupRate;
  const shouldDisplayFxInfo = hasFx && !isCurrencyFromINR;
  const { value: feeValue } = fee;
  const hasFee = feeValue > 0;
  const currencyObject = getCurrency(currency);
  const feeWithFormat = hasFee ? formatAmount(feeValue, currencyObject) : 0;

  function getLogoSrc() {
    const source = logo?.links?.original;

    if (source) {
      return source;
    }
    if (!source && isBankTransfer) {
      return bankTransferPaymentMethodIcon;
    }
    return onlinePaymentMethodIcon;
  }

  return (
    <li className="PaymentOptions-item">
      <label className="PaymentOptions-label">
        <div className="PaymentOptions-body">
          <div className="PaymentOptions-selection">
            <input
              type="radio"
              className="Radio-input"
              name="PaymentOptions"
              onChange={onChange}
              defaultChecked={selected}
            />
            <img
              alt="payment logo"
              className="PaymentOptions-logo"
              src={getLogoSrc()}
            />
          </div>
          <div className="PaymentOptions-summary">
            <span className="PaymentOptions-name" data-testid="name">
              {name}
              {showSupportedLogos && (
                <SupportedLogos data-testid="supported-banks" logos={logos} />
              )}
            </span>
            <div className="PaymentOptions-amounts">
              <span className="PaymentOptions-price" data-testid="price">
                <Amount value={value} currency={currency} />
              </span>
              {hasFee && <Fee value={feeWithFormat} />}
              {interval && <RecurringLabels interval={interval} />}
            </div>
            {shouldDisplayFxInfo && (
              <FXInfo
                markupRate={markupRate}
                exchangeRate={exchangeRate}
                id={paymentOption}
              />
            )}
          </div>
        </div>
      </label>
      {children}
    </li>
  );
};

const Fields = ({
  fields = [],
  onChange,
}: {
  fields: FieldType[];
  onChange?: (name: string, value: string) => void;
}) => {
  const i18n = useTranslations();

  return (
    <div className="PaymentOptions-extra">
      <div className="PaymentOptions-info" role="alert">
        <InfoIcon aria-hidden />
        <span>{i18n.t('checkout.paymentOptions.additionalInfo')}</span>
      </div>
      <div className="grid grid-cols-1 gap-x-2 md:grid-cols-2">
        {fields.map(field => (
          <div
            key={field.id}
            className="m-4 flex flex-col gap-4 relative md:col-span-2 self-start"
          >
            <Field field={field} key={field.id} onChange={onChange} />
          </div>
        ))}
      </div>
    </div>
  );
};

OptionComponent.Fields = Fields;

const mapStateToProps = (state: RootState) => ({
  getCurrency: (code: string) => getCurrency(state, code),
});

const connector = connect(mapStateToProps);

export const Option = connector(OptionComponent);
